import {ListView} from '@github-ui/list-view'
import {CounterLabel} from '@primer/react'
import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'

import styles from './PullsInboxSection.module.css'
import {PullsInboxSectionSkeletonItem} from './PullsInboxSectionSkeletonItem'

interface SkeletonBodyProps {
  numItems: number
  title: string
  ariaLabelledBy?: string
}

/**
 * Body-only loading skeleton for an inbox section. Mirrors the loaded body's box
 * (listContainer + bordered ListView) so the header can stay mounted while the body
 * swaps between skeleton and content without layout shift.
 */
export function PullsInboxSectionSkeletonBody({numItems, title, ariaLabelledBy}: SkeletonBodyProps) {
  return (
    <div className={styles.listContainer} data-testid="pulls-inbox-section-skeleton">
      <ListView
        className="border borderColor-muted rounded-2 overflow-hidden"
        title={title}
        titleHeaderTag="h2"
        ariaLabelledBy={ariaLabelledBy}
      >
        {Array.from({length: numItems}, (_, i) => (
          <PullsInboxSectionSkeletonItem key={i} />
        ))}
      </ListView>
    </div>
  )
}

interface DashboardSectionSkeletonProps {
  name: string
  numItems: number
}

/**
 * Full-section skeleton (its own header) used by the route-level, full-page inbox
 * loading state before any section has mounted.
 */
export function PullsDashboardInboxSectionSkeleton({name, numItems}: DashboardSectionSkeletonProps) {
  const titleId = `pulls-inbox-skeleton-${name.toLowerCase().replace(/\s+/g, '-')}`

  return (
    <section aria-label={`Loading ${name}`}>
      <div className={styles.sectionHeader}>
        <SkeletonBox
          height="var(--control-small-size)"
          width="var(--control-small-size)"
          style={{borderRadius: 'var(--borderRadius-medium)'}}
        />
        <h2 id={titleId} className="h4">
          {name}
        </h2>
        <span className={clsx(styles.count, styles.countLoading)}>
          <CounterLabel>0</CounterLabel>
        </span>
      </div>
      {numItems > 0 ? (
        <PullsInboxSectionSkeletonBody numItems={numItems} title={name} ariaLabelledBy={titleId} />
      ) : null}
    </section>
  )
}
